import React, { ChangeEvent, InvalidEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup, GlobalStyles,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'react-cookie';
import { useAuth } from '../../contexts/AuthContext';
import { TLoginData } from '../../types/api';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import FindIdPopup from '../../components/FindPopup/FindIdPopup';
import FindPwPopup from '../../components/FindPopup/FindPwPopup';
import useAuthStore from '../../store/useAuthStore';
import PasswordChangeModal from 'components/Modal/PasswordChangeModal';
import NoticePopup from '../NoticePopup';

export default function Login() {
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  const [isLoginFailErrMsg, setIsLoginFailErrMsg] = useState<string>(''); // 로그인 실패시, 에러메세지 (정지, 비번변경사용자)
  const [isLoginFail, setIsLoginFail] = useState<boolean>(false); // 로그인 실패시, 알람창 상태관리
  const [lag, setLag] = useState(localStorage.getItem('language') || 'ko');
  const [rememberUserNameCookies, setRememberUserNameCookie, removeRememberUserNameCookie] = useCookies(['rememberUserName']);
  const [isIdRemember, setIsIdRemember] = useState<boolean>(!!rememberUserNameCookies.rememberUserName);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [isPasswordChangeOpen, setIsPasswordChangeOpen] = useState(false);

  useEffect(() => {
    localStorage.removeItem('responseErrorMessage');

    // 로그인 상태면 홈으로 이동
    if (useAuthStore.getState().isLoggedIn) {
      navigate('/');
    }
  }, []);

  useEffect(() => {
    i18n.changeLanguage(lag);
  }, [lag]);

  const [openIdPopup, setOpenIdPopup] = useState(false);
  const [openPwPopup, setOpenPwPopup] = useState(false);

  // ID 팝업 열고 닫는 함수
  const handleOpenIdPopup = () => setOpenIdPopup(true);
  const handleCloseIdPopup = () => setOpenIdPopup(false);
  // PW 팝업 열고 닫는 함수
  const handleOpenPwPopup = () => setOpenPwPopup(true);
  const handleClosePwPopup = () => setOpenPwPopup(false);

  // 로그인 버튼 클릭시 호출되는 함수
  const onFinished = async (data: TLoginData) => {
    // 로그인 시도가 시작되므로, 실패 메시지를 초기화
    setIsLoginFail(false);
    setIsLoginFailErrMsg('');

    const username = data.username;
    const password = data.password;

    if (username && password) {
      try {
        await login(username, password);

        // "이메일 기억하기"가 체크된 경우, 입력한 이메일을 쿠키에 저장 (2,000초 = 약 33분)
        if (isIdRemember) setRememberUserNameCookie('rememberUserName', username, { maxAge: 2000000 });

        navigate('/'); // 로그인 성공 시 루트 경로로 이동
      } catch (error: any) {
        console.error('로그인 실패: ', typeof error, error, Boolean(error === '406'));
        const response = error?.response;
        if (response?.data.code === 406) {
          setIsLoginFail(true);
          setIsLoginFailErrMsg(response.data.message);
        } else if (response?.data.code === 412) {
          setIsLoginFail(true);
          setIsLoginFailErrMsg(response.data.message);
          // setIsPasswordChangeOpen(true); // 비밀번호 변경 팝업 열기 (TODO: 추후에 할 예정)
        }
        setIsLoginFail(true);
      }
    }
  };

  const handleIdRemember = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsIdRemember(checked);
    if (!checked) removeRememberUserNameCookie('rememberUserName');
  };

  const handleLanChange = (event: SelectChangeEvent) => {
    const selectedLanguage = event.target.value as string;
    setLag(selectedLanguage);
    i18n.changeLanguage(selectedLanguage).then((r) => {
      localStorage.setItem('language', selectedLanguage); // 로컬 스토리지에 선택한 언어 저장
    }); // i18next의 언어 변경 메서드 호출
  };

  // 입력값이 유효하지 않을 경우 호출되는 함수 (커스텀 에러 메시지 설정)
  const handleInvalid = (e: InvalidEvent<HTMLInputElement>) => {
    e.target.setCustomValidity(t('validation.required')); // 다국어 에러 메시지 설정
  };

  // 사용자가 입력을 시작하면 호출되는 함수 (에러 메시지 제거 함수)
  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.setCustomValidity(''); // 에러 메시지를 초기화
  };

  return (
    <Container
      maxWidth={false} // maxWidth 속성을 false로 설정하여 기본 maxWidth를 비활성화
      sx={{
        position: 'absolute', // 부모의 제약을 받지 않도록 설정
        top: 0,
        left: 0,
        width: '100%', // 전체 화면 너비
        height: '100%', // 전체 화면 높이
        backgroundColor: '#f0f2f5',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden'
      }}
    >
      <style>
      {`
        @media (max-width: 599px) {
          html {
            margin-left: 100%;
          }
        }

        html, body, #root {
          background-color: #f0f2f5 !important;
        }
      `}
      </style>

      <NoticePopup />

      {/* Box를 Container 안에 위치 */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <img src="/images/ulvac_logo.png" alt="ulvac_logo" style={{ width: '240px', height: 'auto', userSelect: 'none' }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 2, userSelect: 'none' }}>
          {t('ulvac_korea')} <span style={{ fontWeight: 'normal' }}>{t('visitor_registration_system')}</span>
        </Typography>
      </Box>
      <Box
        sx={{
          width: {
            xs: '90%', // 모바일 기기에서는 너비를 90%로 설정
            sm: '480px' // 데스크탑에서는 480px로 설정
          },
          margin: '15px 0',
          display: 'flex',
          justifyContent: 'flex-end' // 오른쪽 정렬
        }}
      >
        <FormControl>
          {/* 언어 선택하는 Select */}
          {/* 기본값은 state의 초기값인 'KOR'이 선택됨 */}
          <Select
            labelId="language-select-label"
            id="language-select"
            value={lag} // 현재 선택된 값
            variant="outlined"
            size="small"
            onChange={handleLanChange} // 값 변경 시 호출되는 핸들러
            sx={{
              width: '80px',
              fontSize: '12px'
            }}
          >
            <MenuItem value={'ko'} sx={{ fontSize: '12px' }}>
              KOR
            </MenuItem>
            <MenuItem value={'en'} sx={{ fontSize: '12px' }}>
              ENG
            </MenuItem>
            <MenuItem value={'ja'} sx={{ fontSize: '12px' }}>
              JPN
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box
        sx={{
          width: {
            xs: '90%', // 모바일 기기에서는 너비를 90%로 설정
            sm: '480px' // 데스크탑에서는 480px로 설정
          },
          minWidth: '300px',
          // height: '480px', // 너비를 100%로 설정하여 유동적으로 변하게 만듦
          marginBottom: {
            xs: 0,
            sm: 10
          },
          padding: '32px',
          boxShadow: 1,
          // border: '1px solid #CCCCCC',
          borderRadius: '8px',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column', // 세로 정렬
          justifyContent: 'center', // 수직 중앙 정렬
          alignItems: 'center' // 수평 중앙 정렬
        }}
      >
        <form
          style={{ width: '100%' }} // form을 박스 너비에 맞춤
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            const data = Object.fromEntries(formData) as unknown as TLoginData;
            onFinished(data);
          }}
        >
          <FormGroup sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label={t('username')}
              name="username"
              variant="outlined"
              defaultValue={rememberUserNameCookies.rememberUserName || ''}
              required
              onInvalid={handleInvalid} // 유효하지 않을 때 호출
              onInput={handleInput} // 입력 시 호출
              InputProps={{
                startAdornment: <UserOutlined style={{ marginRight: '10px' }} />
              }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('password')}
              name="password"
              type="password"
              variant="outlined"
              required
              onInvalid={handleInvalid} // 유효하지 않을 때 호출
              onInput={handleInput} // 입력 시 호출
              InputProps={{
                startAdornment: <LockOutlined style={{ marginRight: '10px' }} />
              }}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={isIdRemember} onChange={handleIdRemember} />}
              label={<Typography variant="body2">{t('remember_username')}</Typography>}
              sx={{
                mt: -1,
                mx: 0
              }}
            />
          </FormGroup>
          {isLoginFail && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {isLoginFailErrMsg || t('login_error')}
            </Alert>
          )}
          <PasswordChangeModal open={isPasswordChangeOpen} onClose={() => setIsPasswordChangeOpen(false)} />
          <Button type="submit" variant="contained" color="primary" fullWidth sx={{ py: '10px', borderRadius: '4px', fontWeight: 'bold' }}>
            {t('login')}
          </Button>
          {/* 반반 중앙 정렬 버튼 그룹 */}
          {/*<Divider sx={{ marginTop: '20px' }} />*/}
          <Box sx={{ display: 'flex', mt: '10px', mb: '-16px', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'end' }}>
              <Button color="inherit" component={Link} to="/signup" sx={{ color: '#7f7f7f', fontSize: '12px' }}>
                {t('sign_up')}
              </Button>
            </Box>
            <Divider orientation="vertical" flexItem variant="middle" />
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button color="inherit" onClick={handleOpenIdPopup} sx={{ color: '#7f7f7f', fontSize: '12px' }}>
                {t('find_id')}
              </Button>
            </Box>
            <Divider orientation="vertical" flexItem variant="middle" />
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'start' }}>
              <Button color="inherit" onClick={handleOpenPwPopup} sx={{ color: '#7f7f7f', fontSize: '12px' }}>
                {t('reset_password')}
              </Button>
            </Box>
          </Box>
          {/*<Divider />*/}
        </form>
      </Box>

      {/* TODO: 임시 문구, 추후 내방신청을 사용하는 사업장이 늘면 삭제 예정 */}
      <Box
        sx={{
          marginTop: {
            xs: 1,
            sm: -8
          },
          fontSize: '14px',
          color: '#555'
        }}
      >본 내방시스템은 한국알박 Technology Center 방문 시 사용하는 시스템입니다.</Box>

      {/* 아이디 찾기  Popup */}
      {openIdPopup && <FindIdPopup isOpen={openIdPopup} handleClose={handleCloseIdPopup} handleOpenPwPopup={handleOpenPwPopup} />}

      {/* 비밀번호 재설정  Popup */}
      {openPwPopup && <FindPwPopup isOpen={openPwPopup} handleClose={handleClosePwPopup} />}
    </Container>
  );
}
